import { useState } from "react";
import type { AdsLibraryAssistDraft } from "@/lib/adsAssistantClient";
import {
  EXAMPLE_BANK_MAX_SELECT,
  useAdsLibraryExampleBankAssist,
} from "@/components/ads-assistant/useAdsLibraryExampleBankAssist";

type Props = {
  scopedOrgId: string | null;
  onApplyTemplateSuggestion: (draft: AdsLibraryAssistDraft, selectedPostIds: string[]) => void;
  onApplyCategorySuggestion: (draft: AdsLibraryAssistDraft) => void;
};

export function AdsLibraryExampleBankAssist(props: Props) {
  const { scopedOrgId, onApplyTemplateSuggestion, onApplyCategorySuggestion } = props;

  const vm = useAdsLibraryExampleBankAssist(scopedOrgId);
  const [expandedPostIds, setExpandedPostIds] = useState<Set<string>>(new Set());

  function toggleExpandPost(postId: string) {
    setExpandedPostIds((prev) => {
      const next = new Set(prev);
      if (next.has(postId)) next.delete(postId);
      else next.add(postId);
      return next;
    });
  }

  if (!scopedOrgId) return null;

  return (
    <div className="mt-4 space-y-3 rounded-lg border border-violet-200/70 bg-white/90 p-3 dark:border-violet-900/50 dark:bg-zinc-950/40">
      <p className="text-[11px] leading-relaxed text-violet-900/90 dark:text-violet-200/85">
        Lists respect your org&apos;s <strong className="font-medium">feed retention</strong> window — older ads drop out of
        the hot feed even though they may still exist elsewhere. Use search to find copy you remember.
      </p>
      <div className="grid gap-2 sm:grid-cols-2">
        <label className="block text-[11px] font-medium text-violet-900 dark:text-violet-200">
          Ads platform <span className="text-red-600 dark:text-red-400">*</span>
          <select
            className="mt-0.5 w-full rounded-lg border border-violet-200/80 bg-white px-2 py-1.5 text-xs dark:border-violet-900/50 dark:bg-zinc-950 dark:text-gray-100"
            value={vm.platform}
            onChange={(e) => {
              vm.setPlatform(e.target.value);
              vm.resetPagingAndSelection();
            }}
          >
            {vm.platformOptions.map((o) => (
              <option key={o.key} value={o.key}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-[11px] font-medium text-violet-900 dark:text-violet-200">
          Location (optional)
          <select
            className="mt-0.5 w-full rounded-lg border border-violet-200/80 bg-white px-2 py-1.5 text-xs dark:border-violet-900/50 dark:bg-zinc-950 dark:text-gray-100"
            value={vm.locationId}
            onChange={(e) => {
              vm.setLocationId(e.target.value);
              vm.resetPagingAndSelection();
            }}
          >
            <option value="">All locations</option>
            {vm.locations.map((loc) => (
              <option key={loc.id} value={loc.id}>
                {loc.name}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-end">
        <label className="block min-w-[200px] flex-1 text-[11px] font-medium text-violet-900 dark:text-violet-200">
          Search post text (substring)
          <input
            className="mt-0.5 w-full rounded-lg border border-violet-200/80 bg-white px-2 py-1.5 text-xs dark:border-violet-900/50 dark:bg-zinc-950 dark:text-gray-100"
            value={vm.searchDraft}
            onChange={(e) => vm.setSearchDraft(e.target.value)}
            placeholder="e.g. steel structures"
          />
        </label>
        <button
          type="button"
          className="rounded-lg border border-violet-300 bg-violet-50 px-3 py-1.5 text-xs font-medium text-violet-900 hover:bg-violet-100 dark:border-violet-800 dark:bg-violet-950/50 dark:text-violet-100 dark:hover:bg-violet-950"
          onClick={() => {
            vm.setSearchApplied(vm.searchDraft);
            vm.resetPagingAndSelection();
          }}
        >
          Search
        </button>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-violet-900 dark:text-violet-200">
        <span>
          Selected {vm.selected.size}/{EXAMPLE_BANK_MAX_SELECT} · Page {vm.page + 1}/{vm.pageCount} ({vm.total} matches)
        </span>
        <div className="flex gap-1">
          <button
            type="button"
            disabled={vm.page <= 0 || vm.listLoading}
            className="rounded border border-violet-200 px-2 py-0.5 text-[11px] disabled:opacity-40 dark:border-violet-800"
            onClick={() => vm.setPage((p) => Math.max(0, p - 1))}
          >
            Prev
          </button>
          <button
            type="button"
            disabled={vm.page >= vm.pageCount - 1 || vm.listLoading}
            className="rounded border border-violet-200 px-2 py-0.5 text-[11px] disabled:opacity-40 dark:border-violet-800"
            onClick={() => vm.setPage((p) => Math.min(vm.pageCount - 1, p + 1))}
          >
            Next
          </button>
        </div>
      </div>

      {vm.listError ? (
        <p className="rounded border border-amber-200 bg-amber-50/90 px-2 py-1 text-xs text-amber-950 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100">
          {vm.listError}
        </p>
      ) : null}

      <ul className="max-h-56 space-y-1 overflow-y-auto rounded border border-violet-100 bg-violet-50/40 p-2 dark:border-violet-900/40 dark:bg-zinc-900/40">
        {vm.listLoading ? <li className="text-xs text-gray-500">Loading…</li> : null}
        {!vm.listLoading && !vm.posts.length ? (
          <li className="text-xs text-gray-500">No posts for these filters.</li>
        ) : null}
        {vm.posts.map((p) => {
          const checked = vm.selected.has(p.id);
          const excerptStr = (p.text_content || "").replace(/\s+/g, " ").trim().slice(0, 140);
          const expanded = expandedPostIds.has(p.id);
          const full = (p.text_content || "").trim() || "(empty)";
          return (
            <li
              key={p.id}
              className="rounded border border-white/80 bg-white dark:border-zinc-700 dark:bg-zinc-950"
            >
              <div className="flex items-start gap-1 px-2 py-1">
                <label className="flex min-w-0 flex-1 cursor-pointer items-start gap-2">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={checked}
                    onChange={() => vm.toggleRow(p.id)}
                    disabled={!checked && vm.selected.size >= EXAMPLE_BANK_MAX_SELECT}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="font-mono text-[10px] text-gray-500">{p.id.slice(0, 8)}…</span>
                    {p.template_example_guard ? (
                      <span className="ml-1 rounded bg-amber-100 px-1 py-0.5 text-[9px] font-semibold text-amber-950 dark:bg-amber-950/60 dark:text-amber-100">
                        Example guard
                      </span>
                    ) : null}
                    <span className="mt-0.5 block text-gray-800 dark:text-gray-200">
                      {expanded ? (
                        <span className="text-gray-500 italic dark:text-gray-400">Full text below.</span>
                      ) : (
                        excerptStr || "(empty)"
                      )}
                    </span>
                  </span>
                </label>
                <button
                  type="button"
                  className="shrink-0 rounded border border-violet-200 px-1.5 py-0.5 text-[11px] font-medium text-violet-900 hover:bg-violet-50 dark:border-violet-800 dark:text-violet-200 dark:hover:bg-violet-950/50"
                  aria-expanded={expanded}
                  aria-label={expanded ? "Collapse full post text" : "Expand full post text"}
                  title={expanded ? "Collapse" : "Full text"}
                  onClick={() => toggleExpandPost(p.id)}
                >
                  ⋯
                </button>
              </div>
              {expanded ? (
                <pre className="max-h-40 overflow-auto whitespace-pre-wrap break-words border-t border-violet-100 px-2 py-1.5 text-[11px] text-gray-800 dark:border-violet-900/40 dark:text-gray-200">
                  {full}
                </pre>
              ) : null}
            </li>
          );
        })}
      </ul>

      <button
        type="button"
        disabled={vm.suggestLoading || vm.selectedIds.length < 1 || !vm.platform}
        className="w-full rounded-lg bg-violet-800 px-3 py-2 text-xs font-semibold text-white hover:bg-violet-900 disabled:opacity-50 sm:w-auto"
        onClick={() => void vm.runSuggestions()}
      >
        {vm.suggestLoading ? "Suggesting…" : "Suggest template & category (parallel)"}
      </button>
      <p className="text-[10px] text-violet-800/80 dark:text-violet-300/80">
        Runs two short AI requests in parallel (template focus + category focus) to stay within serverless time limits.
      </p>

      {vm.suggestError ? (
        <p className="rounded border border-amber-200 bg-amber-50/90 px-2 py-1 text-xs text-amber-950 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100">
          {vm.suggestError}
        </p>
      ) : null}

      <div className="grid gap-3 border-t border-violet-100 pt-3 dark:border-violet-900/40 md:grid-cols-2">
        <div className="rounded-lg border border-violet-200/80 bg-violet-50/50 p-2 dark:border-violet-900/50 dark:bg-violet-950/20">
          <div className="text-[11px] font-semibold text-violet-950 dark:text-violet-100">Template suggestion</div>
          {vm.tplDraft?.template ? (
            <>
              <p className="mt-1 text-[11px] font-medium text-gray-900 dark:text-gray-100">{vm.tplDraft.template.name}</p>
              <pre className="mt-1 max-h-28 overflow-auto whitespace-pre-wrap text-[10px] text-gray-700 dark:text-gray-300">
                {vm.tplDraft.template.body_markdown.slice(0, 1200)}
                {vm.tplDraft.template.body_markdown.length > 1200 ? "…" : ""}
              </pre>
              <button
                type="button"
                className="mt-2 rounded bg-violet-700 px-2 py-1 text-[11px] font-medium text-white hover:bg-violet-800"
                onClick={() => {
                  const tpl = vm.tplDraft?.template;
                  if (tpl) onApplyTemplateSuggestion({ template: tpl }, vm.selectedIds);
                }}
              >
                Apply to template form
              </button>
            </>
          ) : (
            <p className="mt-1 text-[11px] text-gray-500">No template draft yet.</p>
          )}
        </div>
        <div className="rounded-lg border border-violet-200/80 bg-violet-50/50 p-2 dark:border-violet-900/50 dark:bg-violet-950/20">
          <div className="text-[11px] font-semibold text-violet-950 dark:text-violet-100">Category suggestion</div>
          {vm.catDraft?.category ? (
            <>
              <p className="mt-1 text-[11px] text-gray-800 dark:text-gray-200">
                <strong>{vm.catDraft.category.label}</strong>{" "}
                <span className="font-mono text-[10px] text-gray-500">({vm.catDraft.category.slug})</span>
              </p>
              <button
                type="button"
                className="mt-2 rounded bg-violet-700 px-2 py-1 text-[11px] font-medium text-white hover:bg-violet-800"
                onClick={() => {
                  const cat = vm.catDraft?.category;
                  if (cat) onApplyCategorySuggestion({ category: cat });
                }}
              >
                Apply to category form
              </button>
            </>
          ) : (
            <p className="mt-1 text-[11px] text-gray-500">No category draft yet.</p>
          )}
        </div>
      </div>
    </div>
  );
}
